import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  UserGroupIcon, 
  ChartBarIcon, 
  GlobeAltIcon,
  ComputerDesktopIcon,
  ShoppingBagIcon,
  HeartIcon,
  SparklesIcon,
  CheckIcon,
  MegaphoneIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';

interface ProfessionalContextStepProps {
  onNext: () => void;
  onPrevious: () => void;
}

const ProfessionalContextStep: React.FC<ProfessionalContextStepProps> = ({ onNext, onPrevious }) => {
  const { setValue, watch, trigger } = useFormContext();
  
  // Watch form values to sync with local state
  const formIndustry = watch('industry');
  const formJobRole = watch('jobRole');
  const formCompanySize = watch('companySize');
  const formPrimaryUseCase = watch('primaryUseCase');
  
  // Initialize local state from form context
  const [selectedIndustry, setSelectedIndustry] = useState<string>(formIndustry || '');
  const [selectedRole, setSelectedRole] = useState<string>(formJobRole || '');
  const [selectedCompanySize, setSelectedCompanySize] = useState<string>(formCompanySize || '');
  const [selectedUseCases, setSelectedUseCases] = useState<string[]>(
    formPrimaryUseCase ? formPrimaryUseCase.split(',').filter(Boolean) : []
  );

  // Sync local state with form context when form values change
  useEffect(() => {
    if (formIndustry && formIndustry !== selectedIndustry) {
      setSelectedIndustry(formIndustry);
    }
    if (formJobRole && formJobRole !== selectedRole) {
      setSelectedRole(formJobRole);
    }
    if (formCompanySize && formCompanySize !== selectedCompanySize) {
      setSelectedCompanySize(formCompanySize);
    }
    if (formPrimaryUseCase && formPrimaryUseCase !== selectedUseCases.join(',')) {
      setSelectedUseCases(formPrimaryUseCase.split(',').filter(Boolean));
    }
  }, [formIndustry, formJobRole, formCompanySize, formPrimaryUseCase, selectedIndustry, selectedRole, selectedCompanySize, selectedUseCases]);

  const industries = [
    { id: 'technology', name: 'Technology', icon: ComputerDesktopIcon, color: 'bg-blue-500' },
    { id: 'ecommerce', name: 'E-commerce', icon: ShoppingBagIcon, color: 'bg-green-500' },
    { id: 'marketing', name: 'Marketing & Advertising', icon: MegaphoneIcon, color: 'bg-red-500' },
    { id: 'fashion', name: 'Fashion & Beauty', icon: HeartIcon, color: 'bg-pink-500' },
    { id: 'lifestyle', name: 'Lifestyle & Wellness', icon: BeakerIcon, color: 'bg-emerald-500' },
    { id: 'media', name: 'Media & Entertainment', icon: SparklesIcon, color: 'bg-purple-500' },
    { id: 'consulting', name: 'Consulting', icon: BuildingOfficeIcon, color: 'bg-gray-500' },
    { id: 'education', name: 'Education', icon: GlobeAltIcon, color: 'bg-indigo-500' },
    { id: 'other', name: 'Other', icon: BuildingOfficeIcon, color: 'bg-slate-500' },
  ];

  const jobRoles = [
    { id: 'founder', name: 'Founder/CEO', description: 'Business owner or executive' },
    { id: 'marketing', name: 'Marketing', description: 'Marketing manager or specialist' },
    { id: 'product', name: 'Product', description: 'Product manager or developer' },
    { id: 'sales', name: 'Sales', description: 'Sales representative or manager' },
    { id: 'analyst', name: 'Data Analyst', description: 'Business or data analyst' },
    { id: 'consultant', name: 'Consultant', description: 'Business or strategy consultant' },
    { id: 'student', name: 'Student', description: 'Currently studying' },
    { id: 'other', name: 'Other', description: 'Different role' },
  ];

  const companySizes = [
    { id: 'individual', name: 'Just me', description: 'Individual or freelancer' },
    { id: '2-10', name: '2-10', description: 'Startup or small team' },
    { id: '11-50', name: '11-50', description: 'Growing company' },
    { id: '51-200', name: '51-200', description: 'Medium business' },
    { id: '200+', name: '200+', description: 'Large organization' },
  ];

  const useCases = [
    { id: 'market-research', name: 'Market Research' },
    { id: 'content-creation', name: 'Content Creation' },
    { id: 'competitive-analysis', name: 'Competitive Intelligence' },
    { id: 'product-development', name: 'Product Strategy' },
    { id: 'marketing-campaigns', name: 'Marketing Campaigns' },
    { id: 'investment-research', name: 'Investment Research' },
    { id: 'brand-monitoring', name: 'Brand Monitoring' },
    { id: 'social-listening', name: 'Social Listening' },
    { id: 'trend-forecasting', name: 'Trend Forecasting' },
    { id: 'influencer-tracking', name: 'Influencer Tracking' },
    { id: 'customer-insights', name: 'Customer Insights' },
    { id: 'business-intelligence', name: 'Business Intelligence' },
  ];

  const handleIndustrySelect = (industryId: string) => {
    setSelectedIndustry(industryId);
    setValue('industry', industryId);
  };

  const handleRoleSelect = (roleId: string) => {
    setSelectedRole(roleId);
    setValue('jobRole', roleId);
    
    // Auto-select "Just me" for students and individuals
    if (roleId === 'student' || roleId === 'other') {
      setSelectedCompanySize('individual');
      setValue('companySize', 'individual');
    }
  };

  const handleCompanySizeSelect = (sizeId: string) => {
    setSelectedCompanySize(sizeId);
    setValue('companySize', sizeId);
  };

  const handleUseCaseSelect = (useCaseId: string) => {
    const newSelectedUseCases = selectedUseCases.includes(useCaseId)
      ? selectedUseCases.filter(id => id !== useCaseId)
      : [...selectedUseCases, useCaseId];
    
    setSelectedUseCases(newSelectedUseCases);
    setValue('primaryUseCase', newSelectedUseCases.join(','));
  };

  const handleNext = async () => {
    const isValid = await trigger(['industry', 'jobRole', 'companySize', 'primaryUseCase']);
    if (isValid) {
      onNext();
    }
  };

  const canProceed = selectedIndustry && selectedRole && selectedCompanySize && selectedUseCases.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-5"
    >
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Tell us about your work</h3>
        <p className="text-sm text-gray-600">This helps us personalize your experience</p>
      </div>

      {/* Industry Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          What industry do you work in? *
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {industries.map((industry) => {
            const Icon = industry.icon;
            const isSelected = selectedIndustry === industry.id;
            return (
              <motion.button
                key={industry.id}
                type="button"
                onClick={() => handleIndustrySelect(industry.id)}
                className={`relative p-5 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="text-center">
                  <div className={`w-10 h-10 ${industry.color} rounded-lg flex items-center justify-center mx-auto mb-2`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 leading-tight px-2">{industry.name}</p>
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center"
                  >
                    <CheckIcon className="h-4 w-4 text-white" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Job Role Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          What's your primary role? *
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-5xl mx-auto">
          {jobRoles.map((role) => {
            const isSelected = selectedRole === role.id;
            return (
              <motion.button
                key={role.id}
                type="button"
                onClick={() => handleRoleSelect(role.id)}
                className={`relative p-4 rounded-lg border-2 text-left transition-all duration-200 hover:scale-105 ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <div>
                  <p className="text-base font-medium text-gray-900">{role.name}</p>
                  <p className="text-sm text-gray-600">{role.description}</p>
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center"
                  >
                    <CheckIcon className="h-4 w-4 text-white" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Company Size Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          How many people work at your organization? *
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-4xl mx-auto">
          {companySizes.map((size) => {
            const isSelected = selectedCompanySize === size.id;
            return (
              <motion.button
                key={size.id}
                type="button"
                onClick={() => handleCompanySizeSelect(size.id)}
                className={`relative p-5 rounded-lg border-2 transition-all duration-200 hover:scale-105 ${
                  isSelected
                    ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="text-center">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <UserGroupIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  <p className="text-base font-bold text-gray-900">{size.name}</p>
                  <p className="text-sm text-gray-600">{size.description}</p>
                </div>
                {isSelected && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center"
                  >
                    <CheckIcon className="h-4 w-4 text-white" />
                  </motion.div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Primary Use Case Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          How do you plan to use TrendBeat? *
        </label>
        <p className="text-xs text-gray-500 mb-3">Select all that apply</p>
        <div className="flex flex-wrap gap-3 max-w-5xl mx-auto">
          {useCases.map((useCase) => {
            const isSelected = selectedUseCases.includes(useCase.id);
            return (
              <motion.button
                key={useCase.id}
                type="button"
                onClick={() => handleUseCaseSelect(useCase.id)}
                className={`px-5 py-3 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 ${
                  isSelected
                    ? 'bg-purple-500 text-white border-2 border-purple-500'
                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                }`}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                {useCase.name}
              </motion.button>
            );
          })}
        </div>
        {selectedUseCases.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 text-xs text-purple-600"
          >
            {selectedUseCases.length} selected
          </motion.div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end pt-4">
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed}
          className="px-8 py-3 text-lg font-semibold text-white bg-purple-600 border border-transparent rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Continue →
        </button>
      </div>
    </motion.div>
  );
};

export default ProfessionalContextStep;

